"use client";

import React, { useId, useState } from "react";
import { Box, Button, ButtonBase, Collapse, Stack, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScreenRotationAltIcon from "@mui/icons-material/ScreenRotationAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { closestBySize } from "./finish-options";
import { trimMm, type CanvasVariant, type VariantAxis, isSquareVariant } from "./variant-resolver";
import type { CanvasOrientation } from "./canvas-design";
import type { SizeGroup } from "./size-groups";
// One fallback rule for the whole shop — see the comment on `localized`.
// This file used to carry its own copy, which is how two of them end up
// disagreeing about whether Slovenian or English wins.
import { localized } from "src/components/art/artwork-types";
import { formatMoney, variantDisplayPrice } from "./variant-price";
import { RADIUS } from "src/themes/tokens";
import { OptionDensityContext, StepHeading } from "./OptionCard";
import { ChoiceChip, CompactHeading, chipRowSx } from "./CompactOptions";

export interface SizePickerProps {
  /** Ordered best-fit first; the leading group is badged as such. */
  groups: SizeGroup[];
  selectedId: string | null;
  currency: string;
  locale: string;
  /** Declared option axes, used to name what differs between same-size rows. */
  axes: VariantAxis[];
  orientation: CanvasOrientation;
  onOrientationChange: (orientation: CanvasOrientation) => void;
  onSelect: (variant: CanvasVariant) => void;
  t: (key: string) => string;
  /**
   * Phones under a live preview (the map page): a small label and rows of
   * chips instead of a numbered step of tiles.
   */
  compact?: boolean;
  /**
   * Whether "turn portrait / landscape" is offered. A ready-made artwork hangs
   * the way it was painted, so the art pages turn it off.
   */
  canRotate?: boolean;
}

const cm = (mm: number) => Math.round(mm / 10);

/**
 * A qualifier for rows that share dimensions.
 *
 * Two variants can be the same size and differ another way — canvas versus
 * poster at 30 x 40. FinishPicker normally filters those apart before they
 * reach this list, but an axis it does not ask about would still render as
 * two identical rows with different prices. Name the axis
 * that actually differs, in the customer's language. The variant's own name
 * is not usable here: it is seeded to the SKU.
 *
 * Returns null when nothing distinguishes them, which is the normal case.
 */
function qualifier(
  variant: CanvasVariant,
  siblings: CanvasVariant[],
  axes: VariantAxis[],
  locale: string,
): string | null {
  const sameSize = siblings.filter(
    (v) =>
      v.printSpec.widthMm === variant.printSpec.widthMm &&
      v.printSpec.heightMm === variant.printSpec.heightMm,
  );
  if (sameSize.length < 2) return null;

  // The size axis is what the row already shows; any OTHER axis on which these
  // rows disagree is the thing worth naming.
  const differing = axes.filter(
    (a) =>
      a.key !== "size" &&
      new Set(sameSize.map((v) => v.axisValues?.[a.key])).size > 1,
  );

  const parts = differing
    .map((a) => {
      const value = variant.axisValues?.[a.key];
      const def = a.values.find((x) => x.value === value);
      return def ? localized(def.label, locale) : value;
    })
    .filter(Boolean);

  return parts.length ? parts.join(" · ") : null;
}

/**
 * A shape drawn to its real proportions, fitted in a 34px box — so "4:3"
 * and "2:1" look different before either label is read.
 */
function RatioGlyph({
  width,
  height,
  size = 34,
}: {
  width: number;
  height: number;
  size?: number;
}) {
  const scale = size / Math.max(width, height, 1);
  return (
    <Box
      aria-hidden
      sx={(theme) => ({
        width: Math.round(width * scale),
        height: Math.round(height * scale),
        border: 2,
        borderColor: theme.tokens.ink,
        borderRadius: 0.5,
        flexShrink: 0,
      })}
    />
  );
}

/**
 * Shape, orientation and size as numbered steps.
 *
 * Sizes are tiles rather than a dropdown. A closed menu hides every option
 * but one, and opening it, scrolling it and reading 13px rows is exactly the
 * task an older customer struggles with. Here every size and its price is on
 * screen at once, big enough to read and to hit, with the best fit for the
 * photo first.
 */
export default function SizePicker({
  groups,
  selectedId,
  currency,
  locale,
  axes,
  orientation,
  onOrientationChange,
  onSelect,
  t,
  compact = false,
  canRotate = true,
}: SizePickerProps) {
  const baseId = useId();
  const dense = React.useContext(OptionDensityContext);
  const options = groups.flatMap((group) => group.variants);
  const selected = options.find((variant) => variant.id === selectedId);

  // A square has no portrait or landscape: the two cards would produce the
  // same picture and draw the same glyph.
  const squareSelected = isSquareVariant(selected);

  const renderTile = (variant: CanvasVariant, siblings: CanvasVariant[]) => {
    const dimensions = trimMm(variant, orientation);
    const detail = qualifier(variant, siblings, axes, locale);
    // The market's amount from priceCurrencies (euro pair as the fallback),
    // formatted in the customer's locale — see variant-price.ts.
    const price = variantDisplayPrice(variant, currency);
    const isSelected = variant.id === selectedId;
    return (
      <ButtonBase
        key={variant.id}
        role="radio"
        aria-checked={isSelected}
        onClick={() => onSelect(variant)}
        focusRipple
        sx={(theme) => ({
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          gap: 0.25,
          minHeight: dense ? 48 : 60,
          px: dense ? 1.25 : 1.5,
          py: dense ? 0.75 : 1,
          textAlign: "left",
          borderRadius: `${RADIUS.card}px`,
          border: "2px solid",
          borderColor: isSelected ? theme.tokens.brand : theme.palette.grey[300],
          bgcolor: isSelected ? theme.tokens.brandSoft : theme.tokens.surface,
          boxShadow: isSelected ? `0 0 0 1px ${theme.tokens.brand}` : "none",
          "&:hover": { borderColor: theme.tokens.brand },
          "&.Mui-focusVisible": {
            outline: `3px solid ${theme.tokens.brand}`,
            outlineOffset: 2,
          },
        })}
      >
        {/* On the corner rather than inside the tile, so the size never has
            to make room for it and wrap "cm" onto a line of its own. */}
        {isSelected && (
          <CheckCircleIcon
            aria-hidden
            sx={(theme) => ({
              position: "absolute",
              top: -9,
              right: -9,
              fontSize: 22,
              borderRadius: "50%",
              bgcolor: theme.tokens.surface,
              color: theme.tokens.brand,
            })}
          />
        )}
        <Typography
          component="span"
          sx={(theme) => ({
            fontSize: dense ? 14 : 16,
            fontWeight: 800,
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            color: theme.tokens.ink,
          })}
        >
          {cm(dimensions.width)} × {cm(dimensions.height)} cm
        </Typography>
        {detail && (
          <Typography
            component="span"
            sx={(theme) => ({ fontSize: 13, color: theme.tokens.ink2 })}
          >
            {detail}
          </Typography>
        )}
        <Box component="span" sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
          <Typography
            component="span"
            sx={(theme) => ({ fontSize: dense ? 14 : 16, fontWeight: 700, color: theme.tokens.brand })}
          >
            {formatMoney(price.amount, price.currency, locale)}
          </Typography>
          {price.compareAt != null && price.compareAt > price.amount && (
            <Typography
              component="span"
              sx={(theme) => ({
                fontSize: 13,
                color: theme.tokens.ink3,
                textDecoration: "line-through",
              })}
            >
              {formatMoney(price.compareAt, price.currency, locale)}
            </Typography>
          )}
        </Box>
      </ButtonBase>
    );
  };

  // Shape first, then size. However many sizes the shop adds, each lands in
  // its shape's list, so the size step stays a handful of tiles and a new
  // shape costs one more card. The shape is read off the chosen size, so the
  // two can never disagree.
  const currentGroup =
    groups.find((group) => group.variants.some((v) => v.id === selectedId)) ??
    groups[0];
  const sizesOfShape = currentGroup?.variants ?? [];

  const ratioValue = (ratio: string) => {
    const [a, b] = ratio.split(":").map(Number);
    return a && b ? a / b : Number.POSITIVE_INFINITY;
  };
  const shapesInOrder = groups
    .slice()
    .sort((a, b) => ratioValue(a.ratio) - ratioValue(b.ratio));
  const shapeName = (ratio: string) =>
    ratio === "1:1" ? t("canvasShapeSquare") : ratio;

  // A chosen shape other than the photo's own is never folded away.
  const [shapesOpen, setShapesOpen] = useState(false);
  const shapesShown = shapesOpen || currentGroup !== groups[0];

  const linkButtonSx = (theme: Theme) => ({
    minHeight: 44,
    px: 1.25,
    fontSize: 16,
    fontWeight: 700,
    textTransform: "none" as const,
    color: theme.tokens.brand,
  });

  if (compact) {
    return (
      <Box>
        <CompactHeading id={`${baseId}-size`}>{t("canvasSize")}</CompactHeading>
        {options.length === 0 ? (
          <Typography sx={(theme) => ({ color: theme.tokens.ink2 })}>{t("canvasNoSizes")}</Typography>
        ) : (
          <>
            <Box role="radiogroup" aria-labelledby={`${baseId}-size`} sx={chipRowSx}>
              {sizesOfShape.map((variant) => {
                const dimensions = trimMm(variant, orientation);
                const detail = qualifier(variant, sizesOfShape, axes, locale);
                const price = variantDisplayPrice(variant, currency);
                return (
                  <ChoiceChip
                    key={variant.id}
                    selected={variant.id === selectedId}
                    onClick={() => onSelect(variant)}
                    detail={
                      <>
                        {detail ? `${detail} · ` : ""}
                        {formatMoney(price.amount, price.currency, locale)}
                        {price.compareAt != null && price.compareAt > price.amount && (
                          <Box component="s" sx={{ ml: 0.5, fontWeight: 500, opacity: 0.8 }}>
                            {formatMoney(price.compareAt, price.currency, locale)}
                          </Box>
                        )}
                      </>
                    }
                  >
                    {cm(dimensions.width)} × {cm(dimensions.height)} cm
                  </ChoiceChip>
                );
              })}
            </Box>
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.75, ml: -1 }}>
              {canRotate && !squareSelected && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ScreenRotationAltIcon />}
                  onClick={() => onOrientationChange(orientation === "landscape" ? "portrait" : "landscape")}
                  sx={(theme) => ({ ...linkButtonSx(theme), fontSize: 14 })}
                >
                  {t(orientation === "landscape" ? "canvasRotateToPortrait" : "canvasRotateToLandscape")}
                </Button>
              )}
              {groups.length > 1 && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShapesOpen((open) => !open)}
                  aria-expanded={shapesShown}
                  endIcon={shapesShown ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={(theme) => ({ ...linkButtonSx(theme), fontSize: 14 })}
                >
                  {t("canvasOtherShape")}
                </Button>
              )}
            </Stack>
            {groups.length > 1 && (
              <Collapse in={shapesShown}>
                <Box role="radiogroup" aria-label={t("canvasOtherShape")} sx={{ ...chipRowSx, pt: 0.5 }}>
                  {shapesInOrder.map((group) => (
                    <ChoiceChip
                      key={group.ratio}
                      selected={group === currentGroup}
                      onClick={() => {
                        const next = closestBySize(group.variants, selected);
                        if (next) onSelect(next);
                      }}
                    >
                      {shapeName(group.ratio)}
                    </ChoiceChip>
                  ))}
                </Box>
              </Collapse>
            )}
          </>
        )}
      </Box>
    );
  }

  const tileGrid = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
    gap: dense ? 1 : 1.25,
  } as const;

  return (
    <Box>
      <StepHeading id={`${baseId}-size`}>{t("canvasSize")}</StepHeading>
      {options.length === 0 ? (
        <Typography sx={(theme) => ({ color: theme.tokens.ink2 })}>
          {t("canvasNoSizes")}
        </Typography>
      ) : (
        <>
          <Box role="radiogroup" aria-labelledby={`${baseId}-size`} sx={tileGrid}>
            {sizesOfShape.map((variant) => renderTile(variant, sizesOfShape))}
          </Box>

          {/* Orientation and shape follow the photo, so neither is a step.
              These two links are for the few who want something else; the
              rest never have to think in ratios. */}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            {canRotate && !squareSelected && (
              <Button
                variant="text"
                startIcon={<ScreenRotationAltIcon />}
                onClick={() =>
                  onOrientationChange(
                    orientation === "landscape" ? "portrait" : "landscape",
                  )
                }
                sx={linkButtonSx}
              >
                {t(
                  orientation === "landscape"
                    ? "canvasRotateToPortrait"
                    : "canvasRotateToLandscape",
                )}
              </Button>
            )}
            {groups.length > 1 && (
              <Button
                variant="text"
                onClick={() => setShapesOpen((open) => !open)}
                aria-expanded={shapesShown}
                endIcon={shapesShown ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={linkButtonSx}
              >
                {t("canvasOtherShape")}
              </Button>
            )}
          </Stack>

          {groups.length > 1 && (
            <Collapse in={shapesShown}>
              <Typography
                id={`${baseId}-shape`}
                sx={(theme) => ({ mt: 1, mb: 1.5, fontSize: 15, color: theme.tokens.ink2 })}
              >
                {t("canvasOtherShapeHint")}
              </Typography>
              <Box
                role="radiogroup"
                aria-labelledby={`${baseId}-shape`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                  gap: 1.25,
                  pt: 1,
                }}
              >
                {shapesInOrder.map((group) => {
                  const first = group.variants[0];
                  const trim = first ? trimMm(first, orientation) : null;
                  const isSelected = group === currentGroup;
                  // Groups arrive best fit first: that one is the shape the
                  // customer's photo already has, so nothing gets cut off.
                  const likePhoto = group === groups[0];
                  return (
                    <ButtonBase
                      key={group.ratio}
                      role="radio"
                      aria-checked={isSelected}
                      aria-label={
                        likePhoto
                          ? `${shapeName(group.ratio)} — ${t("canvasShapeLikePhoto")}`
                          : shapeName(group.ratio)
                      }
                      onClick={() => {
                        const next = closestBySize(group.variants, selected);
                        if (next) onSelect(next);
                      }}
                      focusRipple
                      sx={(theme) => ({
                        position: "relative",
                        minHeight: 56,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        px: 1,
                        borderRadius: `${RADIUS.card}px`,
                        border: "2px solid",
                        borderColor: isSelected ? theme.tokens.brand : theme.palette.grey[300],
                        bgcolor: isSelected ? theme.tokens.brandSoft : theme.tokens.surface,
                        boxShadow: isSelected ? `0 0 0 1px ${theme.tokens.brand}` : "none",
                        "&:hover": { borderColor: theme.tokens.brand },
                        "&.Mui-focusVisible": {
                          outline: `3px solid ${theme.tokens.brand}`,
                          outlineOffset: 2,
                        },
                      })}
                    >
                      {likePhoto && (
                        <Box
                          component="span"
                          aria-hidden
                          sx={(theme) => ({
                            position: "absolute",
                            top: -10,
                            left: "50%",
                            transform: "translateX(-50%)",
                            px: 0.75,
                            py: 0.1,
                            borderRadius: 999,
                            bgcolor: theme.tokens.ok,
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: 1.6,
                            whiteSpace: "nowrap",
                          })}
                        >
                          {t("canvasRecommended")}
                        </Box>
                      )}
                      {trim && <RatioGlyph width={trim.width} height={trim.height} size={24} />}
                      <Typography
                        component="span"
                        sx={(theme) => ({
                          fontSize: 16,
                          fontWeight: 800,
                          whiteSpace: "nowrap",
                          color: theme.tokens.ink,
                        })}
                      >
                        {shapeName(group.ratio)}
                      </Typography>
                    </ButtonBase>
                  );
                })}
              </Box>
            </Collapse>
          )}
        </>
      )}
    </Box>
  );
}
